/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback } from 'react';
import { Asset, AssetMediaType, TvAsset, TimeUtils, AssetType, AssetUtils } from '@wit/vtv-x-foundation-lib';
import Small from './Small';
import SmallScheduled from './SmallScheduled';
import ChannelCard from './ChannelCard';
import Portrait from './Portrait';
import Landscape from './Landscape';

export enum CARDS_DYNAMIC_DIRECTION {
  VERTICAL,
  HORIZONTAL,
}

interface Props {
  asset: Asset;
  onAssetClick: (asset: Asset) => void;
  showSeriesInfo?: boolean;
  identifyScheduled?: boolean;
  dynamicDirection?: CARDS_DYNAMIC_DIRECTION;
  setIsMouseEnter?: (value: boolean) => void;
  ctxId: number | string;
}

export default function Card({
  asset,
  onAssetClick,
  showSeriesInfo,
  identifyScheduled,
  dynamicDirection,
  setIsMouseEnter,
  ctxId,
}: Props): JSX.Element | null {
  const card = useCallback(() => {
    if (
      AssetUtils.isTvAsset(asset) &&
      identifyScheduled &&
      asset?.startDate &&
      TimeUtils.isAfterNow(asset?.startDate)
    ) {
      return <SmallScheduled onAssetClick={onAssetClick} asset={asset as TvAsset} ctxId={ctxId} />;
    }

    if ((AssetUtils.isTvAsset(asset) || AssetUtils.isEpisode(asset)) && AssetUtils.hasValidSeriesId(asset)) {
      return <Landscape onAssetClick={onAssetClick} asset={asset} showSeriesInfo={showSeriesInfo} ctxId={ctxId} />;
    }

    if (asset.mediaType === AssetMediaType.LINEAR) {
      return <ChannelCard onAssetClick={onAssetClick} asset={asset} ctxId={ctxId} />;
    }

    switch (asset.type) {
      case AssetType.WIDE:
      case AssetType.LANDSCAPE:
        return <Landscape onAssetClick={onAssetClick} asset={asset} showSeriesInfo={showSeriesInfo} ctxId={ctxId} />;
      case AssetType.PORTRAIT:
        return <Portrait onAssetClick={onAssetClick} asset={asset} ctxId={ctxId} />;
      case AssetType.CHANNEL:
      case AssetType.SQUARE:
        return <Small onAssetClick={onAssetClick} asset={asset} showSeriesInfo={showSeriesInfo} ctxId={ctxId} />;
      default:
        return null;
    }
  }, [asset, ctxId, identifyScheduled, onAssetClick, showSeriesInfo]);

  return (
    <div
      className={`dynamic-content-card ${asset.type === AssetType.PORTRAIT ? 'portrait' : 'wide'} ${
        dynamicDirection === CARDS_DYNAMIC_DIRECTION.HORIZONTAL ? 'horizontal' : ''
      }`}
      onMouseEnter={() => {
        if (setIsMouseEnter) {
          setIsMouseEnter(false);
        }
      }}
      onMouseLeave={() => {
        if (setIsMouseEnter) {
          setIsMouseEnter(true);
        }
      }}
    >
      {card()}
    </div>
  );
}
